import { useContext } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import StaffPageShell from '../components/StaffPageShell';
import MessageDisplay from '../components/MessageDisplay';
import useFeatureFlags from '../hooks/useFeatureFlags';
import '../styles/notifications.css';

export default function AdminFeatureFlagsPage() {
  const { user } = useContext(AuthContext);
  const authUser = user?.user || user;
  const {
    canManage,
    groups,
    loading,
    savingKey,
    error,
    success,
    updateFlag,
  } = useFeatureFlags(authUser);

  if (!canManage) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <StaffPageShell
      title="Feature flags"
      subtitle="Enable or disable product features — including notification events"
      wide
    >
      <p className="staff-page-lead">
        Use this module to turn features on or off without a deploy. Notification channel/SMTP
        settings stay under{' '}
        <Link to="/admin/notifications" style={{ color: 'var(--color-primary)' }}>
          Notifications
        </Link>
        ; event automation is controlled here.
      </p>

      <MessageDisplay error={error} success={success} />

      {loading ? <p className="notif-muted">Loading flags…</p> : null}

      {!loading &&
        groups.map((g) => (
          <section key={g.group} className="notif-panel" style={{ marginBottom: '1rem' }}>
            <h2 style={{ margin: '0 0 0.75rem', fontSize: '1.05rem' }}>{g.group}</h2>
            {(g.items || []).map((flag) => {
              const meta = flag.metadata && typeof flag.metadata === 'object' ? flag.metadata : {};
              const isEvent = flag.key.startsWith('notifications.event.');
              const busy = savingKey === flag.key;
              return (
                <div
                  key={flag.key}
                  style={{
                    display: 'grid',
                    gap: '0.35rem',
                    padding: '0.65rem 0',
                    borderBottom: '1px solid var(--color-border)',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '0.75rem 1.25rem',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <div style={{ minWidth: '16rem', flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{flag.label}</div>
                      {flag.description ? (
                        <div className="notif-muted" style={{ marginTop: '0.2rem' }}>
                          {flag.description}
                        </div>
                      ) : null}
                      <code className="notif-muted" style={{ fontSize: '0.7rem' }}>
                        {flag.key}
                      </code>
                    </div>
                    <label className="notif-check-row" style={{ margin: 0 }}>
                      <input
                        type="checkbox"
                        checked={Boolean(flag.enabled)}
                        disabled={busy}
                        onChange={(e) => updateFlag(flag.key, { enabled: e.target.checked })}
                      />
                      <span>{flag.enabled ? 'On' : 'Off'}</span>
                    </label>
                  </div>
                  {isEvent ? (
                    <label className="notif-check-row" style={{ marginLeft: '0.1rem' }}>
                      <input
                        type="checkbox"
                        checked={Boolean(meta.onImport)}
                        disabled={busy || !flag.enabled}
                        onChange={(e) =>
                          updateFlag(flag.key, {
                            metadata: { onImport: e.target.checked },
                          })
                        }
                      />
                      <span className="notif-muted">Also fire during Excel/CSV import</span>
                    </label>
                  ) : null}
                </div>
              );
            })}
          </section>
        ))}

      {!loading && groups.length === 0 ? (
        <p className="notif-muted">No feature flags found.</p>
      ) : null}
    </StaffPageShell>
  );
}
